import React from "react";
import { TreePine, Sparkles, Award, Sprout } from "lucide-react";

const NEW_WINDOW_DAYS = 45;

const monthYear = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const isRecent = (iso) => {
  try {
    return (Date.now() - new Date(iso).getTime()) / 86400000 <= NEW_WINDOW_DAYS;
  } catch {
    return false;
  }
};

const MilestoneRow = ({ m }) => {
  const recent = m.achieved && m.key !== "signup" && isRecent(m.date);
  return (
    <div
      data-testid={`earth-milestone-${m.key}`}
      className="flex items-center gap-3 py-2.5"
      style={{ opacity: m.achieved ? 1 : 0.45 }}
    >
      <span
        className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full"
        style={{
          background: m.achieved ? "rgba(57,255,20,0.14)" : "rgba(255,255,255,0.04)",
          border: `1px solid ${m.achieved ? "rgba(57,255,20,0.5)" : "rgba(255,255,255,0.12)"}`,
        }}
      >
        <TreePine className="w-4 h-4" style={{ color: m.achieved ? "#39FF14" : "#71717a" }} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white text-sm font-semibold">{m.title}</span>
          {m.bonus && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em]" style={{ background: "rgba(0,229,255,0.1)", color: "#00E5FF", border: "1px solid rgba(0,229,255,0.3)" }}>
              <Award className="w-2.5 h-2.5" /> Health bonus
            </span>
          )}
          {recent && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] animate-pulse" style={{ background: "rgba(57,255,20,0.14)", color: "#39FF14" }}>
              <Sparkles className="w-2.5 h-2.5" /> New
            </span>
          )}
        </div>
        <div className="text-zinc-500 text-xs">{m.detail}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-sm font-bold" style={{ color: m.achieved ? "#39FF14" : "#71717a" }}>
          +{m.trees} tree{m.trees > 1 ? "s" : ""}
        </div>
        <div className="font-mono text-[10px] text-zinc-600">
          {m.achieved ? monthYear(m.date) : `Est. ${monthYear(m.date)}`}
        </div>
      </div>
    </div>
  );
};

/** "Your grove" — milestone tree-planting timeline for the signed-in owner. */
export const EarthGrove = ({ grove }) => {
  if (!grove?.exists) return null;
  const shown = Math.min(grove.trees_earned, 12);
  const overflow = grove.trees_earned - shown;
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 pb-12" data-testid="earth-grove">
      <div className="rounded-3xl p-6 sm:p-8" style={{ background: "rgba(6,14,6,0.9)", border: "1px solid rgba(57,255,20,0.18)" }}>
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Totals */}
          <div className="lg:w-72 shrink-0">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] mb-4" style={{ color: "#39FF14" }}>
              <Sprout className="w-3.5 h-3.5" /> Your grove
            </div>
            <div className="text-white text-5xl font-bold" data-testid="earth-grove-total">
              {grove.trees_earned}
              <span className="ml-2 text-sm font-mono font-normal text-zinc-500">trees planted</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1" aria-hidden="true">
              {Array.from({ length: shown }).map((_, i) => (
                <TreePine key={i} className="w-4 h-4" style={{ color: "#39FF14", opacity: 0.5 + 0.5 * ((i + 1) / shown) }} />
              ))}
              {overflow > 0 && <span className="font-mono text-xs text-zinc-500">+{overflow}</span>}
            </div>
            {grove.next && (
              <div className="mt-6" data-testid="earth-grove-next">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 mb-1.5">Next milestone</div>
                <div className="text-white text-sm font-semibold">{grove.next.title}</div>
                <div className="text-zinc-500 text-xs mb-2">
                  {grove.next.months_remaining} months away · +{grove.next.trees} tree{grove.next.trees > 1 ? "s" : ""}
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.07)" }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${grove.next.progress_pct}%`, background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
                  />
                </div>
              </div>
            )}
          </div>
          {/* Timeline */}
          <div className="flex-1 min-w-0 divide-y" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
            {grove.milestones.map((m) => (
              <MilestoneRow key={m.key} m={m} />
            ))}
          </div>
        </div>
        <p className="mt-5 font-mono text-[10px] text-zinc-600">
          Trees are planted through verified reforestation partners as each battery-health anniversary is reached.
          Health-bonus trees are awarded while the pack holds ≥ 90% state of health.
        </p>
      </div>
    </section>
  );
};
